import { useCallback, useRef } from 'react';
import { fetchGuideMediaFile } from '../../../../api/stays-guide-client';
import type { GuideMedia } from '../../../../interfaces/stays-guide';

/**
 * SHARE A GUIDE PHOTO FROM THE HOST'S EDITOR (plan S7.8, the F-17 share flow).
 *
 * iOS Safari only lets navigator.share() run inside the tap that asked for
 * it; an await on the network in between spends that permission. So the bytes
 * are fetched AHEAD — when the tile takes focus or the pointer — from the host
 * file route, and the tap builds the File from what is already here:
 *   canShare({files}) → share(); the person closing the sheet (AbortError) is
 *   silent; no file share (most desktops) → a download.
 */

export type ShareOutcome = 'shared' | 'aborted' | 'downloaded';

const extensionOf = (type: string): string => (type === 'image/png' ? 'png' : type === 'image/webp' ? 'webp' : 'jpg');

export const shareFileName = (title: string | null | undefined, type: string): string => {
  const base = String(title || '')
    .replace(/[\\/:*?"<>|.]+/g, '')
    .trim()
    .replace(/\s+/g, '-');

  return `${base || 'photo'}.${extensionOf(type)}`;
};

const download = (file: File) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
};

type ShareNavigator = Navigator & { canShare?: (data: ShareData) => boolean };

export const useGuideShare = (listingId: string) => {
  const ready = useRef(new Map<string, Blob>());
  const pending = useRef(new Map<string, Promise<Blob>>());

  const load = useCallback(
    (id: string): Promise<Blob> => {
      const have = ready.current.get(id);
      if (have) return Promise.resolve(have);
      const inFlight = pending.current.get(id);
      if (inFlight) return inFlight;
      const p = fetchGuideMediaFile(listingId, id).then(
        blob => {
          ready.current.set(id, blob);
          pending.current.delete(id);

          return blob;
        },
        err => {
          pending.current.delete(id);
          throw err;
        }
      );
      pending.current.set(id, p);

      return p;
    },
    [listingId]
  );

  /** Fetch ahead; a failure here is retried by the tap. */
  const prefetch = useCallback(
    (id: string) => {
      if (ready.current.has(id) || pending.current.has(id)) return;
      load(id).catch(() => undefined);
    },
    [load]
  );

  const isReady = useCallback((id: string) => ready.current.has(id), []);

  const share = useCallback(
    async (m: GuideMedia, name: string): Promise<ShareOutcome> => {
      const blob = ready.current.get(m.id) || (await load(m.id));
      const type = m.content_type || blob.type || 'image/jpeg';
      const file = new File([blob], shareFileName(m.title, type), { type });
      const nav = (typeof navigator !== 'undefined' ? navigator : null) as ShareNavigator | null;
      if (nav && typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
        try {
          await nav.share({ files: [file], title: name });

          return 'shared';
        } catch (err) {
          if (err && (err as { name?: string }).name === 'AbortError') return 'aborted';
          // NotAllowedError (the tap's permission was spent) and the like: save it instead.
        }
      }
      download(file);

      return 'downloaded';
    },
    [load]
  );

  return { prefetch, share, isReady };
};
